import { useToast } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import type { JSONContent } from '@tiptap/core';
import {
  SAVE_STATUS_DISPLAY_DURATION,
  TOAST_ERROR_DURATION,
  TOAST_IS_CLOSABLE,
  TOAST_POSITION,
} from '@web-app/components/TipTap/constants';
import {
  useSaveSessionNote,
  useSaveSessionNoteImmediate,
} from '@web-app/hooks/mutations/session-notes/saveSessionNote';
import { AppointmentResponse } from '@web-app/hooks/queries/appointment-by-room-name/types';
import { Note } from '@web-app/server/api/types';
import { getSessionNoteData } from '@web-app/utils/api/session-notes/utils';
import { useCallback, useState } from 'react';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseAutoSaveWithStatusProps {
  roomId: string | undefined;
  appointment?: AppointmentResponse;
  sessionNote?: Note;
  setSessionNote: (
    noteOrUpdater:
      | Note
      | undefined
      | ((prev: Note | undefined) => Note | undefined),
  ) => void;
  organizationId?: string;
  logPrefix?: string;
}

/**
 * Custom hook for auto-saving with visual status tracking
 * Used by TipTapTab to provide immediate feedback on save operations
 */
export const useAutoSaveWithStatus = ({
  roomId,
  appointment,
  sessionNote,
  setSessionNote,
  organizationId,
  logPrefix = 'AutoSave',
}: UseAutoSaveWithStatusProps) => {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [lastSaveError, setLastSaveError] = useState<string | null>(null);

  const toast = useToast({
    position: TOAST_POSITION,
    isClosable: TOAST_IS_CLOSABLE,
    duration: TOAST_ERROR_DURATION,
  });

  const handleSaveSuccess = useCallback(() => {
    setSaveStatus('saved');
    setLastSaveError(null);

    // Reset to idle after displaying saved status
    setTimeout(() => setSaveStatus('idle'), SAVE_STATUS_DISPLAY_DURATION);
  }, []);

  const handleSaveError = useCallback(
    (error: Error) => {
      setSaveStatus('error');
      setLastSaveError(error.message || 'Failed to save note');
      toast({
        title: 'Failed to save note',
        description: 'Your changes may not have been saved. Please try again.',
        status: 'error',
      });

      datadogLogs.logger.error(
        `${logPrefix} - Failed to save session note`,
        {
          roomId,
          error: error.message,
          organizationId,
          providerId: appointment?.provider_id,
          clientId: appointment?.client_id,
        },
        error,
      );
    },
    [toast, roomId, organizationId, appointment, logPrefix],
  );

  const { mutate: saveProviderNotes } = useSaveSessionNote();
  const { mutate: saveProviderNotesImmediate } = useSaveSessionNoteImmediate({
    onSuccess: (note: Note) => {
      setSessionNote(note);
      handleSaveSuccess();
    },
    onError: handleSaveError,
  });

  const saveWithStatus = useCallback(
    (content: string, contentJson?: JSONContent) => {
      setSaveStatus('saving');
      const updatedNotes = getSessionNoteData({
        data: {
          providerNotes: content,
          ...(contentJson ? { providerNotesJson: contentJson } : {}),
        },
        sessionNote,
        roomId,
        appointment,
      });
      setSessionNote((s) =>
        s ? { ...s, ...updatedNotes } : (updatedNotes as Note),
      );

      saveProviderNotes(updatedNotes, {
        onSuccess: handleSaveSuccess,
        onError: handleSaveError,
      });
    },
    [
      appointment,
      sessionNote,
      roomId,
      setSessionNote,
      saveProviderNotes,
      handleSaveSuccess,
      handleSaveError,
    ],
  );

  const saveImmediateWithStatus = useCallback(
    (data: Partial<Note>) => {
      setSaveStatus('saving');
      // Ensure sessionNote is never undefined (convert to null if needed)
      // getSessionNoteData always returns a valid Note structure, so we can safely cast
      const noteData: Note = {
        ...data,
        sessionNote: data.sessionNote ?? null,
      };
      saveProviderNotesImmediate(noteData);
    },
    [saveProviderNotesImmediate],
  );

  const clearContent = useCallback(() => {
    if (!appointment?.provider_id || !appointment?.client_id) return;

    const updatedNotes = getSessionNoteData({
      data: {
        providerNotes: '',
        providerNotesJson: undefined,
      },
      sessionNote,
      roomId,
      appointment,
    });
    setSessionNote((s) =>
      s ? { ...s, ...updatedNotes } : (updatedNotes as Note),
    );
    saveProviderNotes(updatedNotes);
  }, [appointment, sessionNote, roomId, setSessionNote, saveProviderNotes]);

  return {
    saveStatus,
    lastSaveError,
    saveWithStatus,
    saveImmediateWithStatus,
    clearContent,
    saveProviderNotes, // Export raw mutation for component-level debouncing
    handleSaveSuccess,
    handleSaveError,
    setSaveStatus,
  };
};
